/**
 * Wire protocol of the screen-share annotation overlay.
 *
 * Everything rides the LiveKit data channel under one topic. There is no
 * server side to this feature: the presenter's client is the only authority,
 * and it only decides policy — nothing is stored anywhere.
 *
 * ⚠️ Two rules hold this together and both are load-bearing:
 *
 * 1. `decode` never throws. It runs inside the room's DataReceived handler, so
 *    one malformed packet from one client would otherwise take the overlay
 *    down for everybody in the call.
 * 2. Nothing arriving from the network is trusted for size or range. Every
 *    number is clamped and every array is cut to a ceiling here, before the
 *    session layer ever sees it — a remote peer must not be able to hand us a
 *    million-point stroke.
 */
import { clampFrame } from "./geometry";

export const ANN_TOPIC = "ann";

/** Bumped only on an incompatible change; travels in the handshake alone. */
export const ANN_VERSION = 1;

/**
 * Points per `pts` message. At 20 Hz a hand produces a couple of dozen, so
 * this is roomy; it exists to bound what a peer can send in one packet.
 */
export const MAX_POINTS_PER_MESSAGE = 256;

/** Packets accepted from one participant per second. */
export const MAX_PACKETS_PER_SECOND = 40;

/** Points accepted from one participant per second. */
export const MAX_POINTS_PER_SECOND = 2000;

/** Live points kept per participant before their oldest strokes are dropped. */
export const MAX_LIVE_POINTS = 2000;

/** Strokes per snapshot packet; the rest go in the next chunk. */
export const MAX_SNAPSHOT_STROKES = 40;

/** Points per snapshot packet, so one message stays well clear of any limit. */
export const MAX_SNAPSHOT_POINTS = 1500;

const MAX_IDENTITY_LENGTH = 64;
const MAX_BLOCKED = 64;
const MAX_TRACK_LENGTH = 64;
const MAX_TTL_MS = 10 * 60 * 1000;

/** SID of the screen-share publication being annotated. */
export type TrackKey = string;

export interface HelloMessage {
  k: "hello";
  t: TrackKey;
  v: number;
}

export interface PolicyMessage {
  k: "policy";
  t: TrackKey;
  /** May anyone but the presenter draw? */
  draw: boolean;
  /** Are other people's cursors shown at all? */
  cursor: boolean;
  /** Identities the presenter silenced individually. */
  blocked: string[];
}

export interface CursorMessage {
  k: "cur";
  t: TrackKey;
  x: number;
  y: number;
  /** Laser pointer held down — draw a fading trail instead of a plain dot. */
  l?: 1;
}

export interface CursorOutMessage {
  k: "out";
  t: TrackKey;
}

export interface PointsMessage {
  k: "pts";
  t: TrackKey;
  /** Stroke id, unique per author — the author is known from the packet. */
  id: number;
  /** Chunk number within the stroke, from 0. */
  n: number;
  /** Flat [x0, y0, x1, y1, …]: half the JSON of an array of objects. */
  p: number[];
  /** Palette index; absent means "the author's own colour". */
  c?: number;
  /** Width in frame units (fraction of frame height). */
  w?: number;
  /** Lifetime in ms after the stroke ends; 0 means it stays until cleared. */
  ttl?: number;
  /** Shape kind; absent means freehand. See SHAPE_* below. */
  s?: number;
}

/**
 * Виды фигур.
 *
 * ⚠️ Фигура задаётся ДВУМЯ точками и присылается целиком каждый раз с
 * `n: 0` — получатель на нулевом куске заменяет точки, а не дописывает. Так
 * «резиновая» рамка тянется у всех по ходу, а не появляется только после
 * отпускания кнопки.
 */
export const SHAPE_FREE = 0;
export const SHAPE_ARROW = 1;
export const SHAPE_RECT = 2;
export const SHAPE_ELLIPSE = 3;
export const MAX_SHAPE = SHAPE_ELLIPSE;

export interface StrokeEndMessage {
  k: "end";
  t: TrackKey;
  id: number;
}

export interface UndoMessage {
  k: "undo";
  t: TrackKey;
  id: number;
}

/** One finished stroke as it travels in a snapshot. */
export interface SnapshotStroke {
  /** Author identity — kept so colour, undo and per-author clear still work. */
  a: string;
  id: number;
  p: number[];
  c?: number;
  w?: number;
  /** Shape kind; absent means freehand. */
  s?: number;
}

/**
 * Everything permanent that is already on the board, sent by the presenter to
 * somebody who just arrived.
 *
 * Only permanent ink travels: vanishing strokes are gone in seconds, and
 * shipping them would race their own expiry.
 */
export interface SnapshotMessage {
  k: "snap";
  t: TrackKey;
  /** Chunk index from 0 and total count — a full board exceeds one packet. */
  i: number;
  n: number;
  s: SnapshotStroke[];
}

export interface ClearMessage {
  k: "clear";
  t: TrackKey;
  /** Whose ink to drop; absent means everything on this track. */
  who?: string;
}

export type AnnMessage =
  | HelloMessage
  | PolicyMessage
  | CursorMessage
  | CursorOutMessage
  | PointsMessage
  | StrokeEndMessage
  | UndoMessage
  | ClearMessage
  | SnapshotMessage;

/** Only these travel on the lossy channel; everything else must be reliable. */
export function isReliable(message: AnnMessage): boolean {
  return message.k !== "cur";
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encode(message: AnnMessage): Uint8Array {
  return encoder.encode(JSON.stringify(message));
}

/**
 * Parse an incoming packet, or undefined if it is not something we understand.
 *
 * Unknown kinds are undefined rather than an error: the topic is shared and a
 * future client may well send messages this one has never heard of.
 */
export function decode(payload: Uint8Array): AnnMessage | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(decoder.decode(payload));
  } catch {
    return undefined;
  }

  if (typeof raw !== "object" || raw === null) return undefined;
  const m = raw as Record<string, unknown>;

  const t = text(m.t, MAX_TRACK_LENGTH);
  if (!t) return undefined;

  switch (m.k) {
    case "hello":
      return { k: "hello", t, v: whole(m.v, 0, 1000) ?? 0 };

    case "policy":
      return {
        k: "policy",
        t,
        draw: m.draw === true,
        cursor: m.cursor === true,
        blocked: identities(m.blocked),
      };

    case "cur": {
      const x = coordinate(m.x);
      const y = coordinate(m.y);
      if (x === undefined || y === undefined) return undefined;
      return m.l === 1 ? { k: "cur", t, x, y, l: 1 } : { k: "cur", t, x, y };
    }

    case "out":
      return { k: "out", t };

    case "pts": {
      const id = whole(m.id, 0, Number.MAX_SAFE_INTEGER);
      const n = whole(m.n, 0, Number.MAX_SAFE_INTEGER);
      const p = points(m.p);
      if (id === undefined || n === undefined || !p) return undefined;

      const message: PointsMessage = { k: "pts", t, id, n, p };

      const c = whole(m.c, 0, 63);
      if (c !== undefined) message.c = c;

      const w = whole(m.w, 1, 4095);
      if (w !== undefined) message.w = w;

      const ttl = whole(m.ttl, 0, MAX_TTL_MS);
      if (ttl !== undefined) message.ttl = ttl;

      const shape = whole(m.s, 0, MAX_SHAPE);
      if (shape) message.s = shape;

      return message;
    }

    case "end":
    case "undo": {
      const id = whole(m.id, 0, Number.MAX_SAFE_INTEGER);
      if (id === undefined) return undefined;
      return { k: m.k, t, id };
    }

    case "clear": {
      const who = text(m.who, MAX_IDENTITY_LENGTH);
      return who ? { k: "clear", t, who } : { k: "clear", t };
    }

    case "snap": {
      const i = whole(m.i, 0, 4095);
      const n = whole(m.n, 1, 4096);
      if (i === undefined || n === undefined || !Array.isArray(m.s)) {
        return undefined;
      }

      const strokes: SnapshotStroke[] = [];
      for (const entry of m.s.slice(0, MAX_SNAPSHOT_STROKES)) {
        if (typeof entry !== "object" || entry === null) continue;
        const item = entry as Record<string, unknown>;

        const a = text(item.a, MAX_IDENTITY_LENGTH);
        const id = whole(item.id, 0, Number.MAX_SAFE_INTEGER);
        const p = points(item.p);
        if (!a || id === undefined || !p) continue;

        const stroke: SnapshotStroke = { a, id, p };
        const c = whole(item.c, 0, 63);
        if (c !== undefined) stroke.c = c;
        const w = whole(item.w, 1, 4095);
        if (w !== undefined) stroke.w = w;
        const shape = whole(item.s, 0, MAX_SHAPE);
        if (shape) stroke.s = shape;
        strokes.push(stroke);
      }

      return { k: "snap", t, i, n, s: strokes };
    }

    default:
      return undefined;
  }
}

/**
 * Split a run of points into messages.
 *
 * Chunks overlap by one point on purpose: without it the receiver has no
 * segment joining the last point of one chunk to the first of the next, and a
 * fast stroke comes out as a dashed line.
 */
export function chunkPoints(
  flat: number[],
  max: number = MAX_POINTS_PER_MESSAGE,
): number[][] {
  const limit = Math.max(2, max);
  if (flat.length <= limit * 2) return flat.length ? [flat.slice()] : [];

  const chunks: number[][] = [];
  for (let start = 0; start < flat.length - 2; start += (limit - 1) * 2) {
    chunks.push(flat.slice(start, start + limit * 2));
  }
  return chunks;
}

/**
 * Split a board into snapshot packets.
 *
 * Bounded by both stroke count and total points: one long stroke can outweigh
 * forty short ones, and it is the points that make a packet big.
 */
export function chunkSnapshot(strokes: SnapshotStroke[]): SnapshotStroke[][] {
  const chunks: SnapshotStroke[][] = [];
  let current: SnapshotStroke[] = [];
  let points = 0;

  for (const stroke of strokes) {
    const size = stroke.p.length / 2;
    if (
      current.length &&
      (current.length >= MAX_SNAPSHOT_STROKES ||
        points + size > MAX_SNAPSHOT_POINTS)
    ) {
      chunks.push(current);
      current = [];
      points = 0;
    }
    current.push(stroke);
    points += size;
  }

  if (current.length) chunks.push(current);
  return chunks;
}

export type LimitVerdict = "ok" | "dropped" | "dropped-first";

/**
 * Per-participant ceiling on what we are willing to render.
 *
 * A client with a stuck loop — or one deliberately spraying — must not be able
 * to freeze everyone else's tab. Counters reset on a one-second window: cheap,
 * and precision buys nothing here.
 */
export class ReceiveLimiter {
  #packets: number;
  #points: number;
  #now: () => number;
  #state = new Map<
    string,
    { since: number; packets: number; points: number; warned: boolean }
  >();

  constructor(options?: {
    packetsPerSecond?: number;
    pointsPerSecond?: number;
    now?: () => number;
  }) {
    this.#packets = options?.packetsPerSecond ?? MAX_PACKETS_PER_SECOND;
    this.#points = options?.pointsPerSecond ?? MAX_POINTS_PER_SECOND;
    this.#now = options?.now ?? (() => Date.now());
  }

  accept(identity: string, points = 0): LimitVerdict {
    const now = this.#now();
    let bucket = this.#state.get(identity);

    if (!bucket || now - bucket.since >= 1000) {
      bucket = { since: now, packets: 0, points: 0, warned: bucket?.warned ?? false };
      this.#state.set(identity, bucket);
    }

    if (bucket.packets + 1 > this.#packets || bucket.points + points > this.#points) {
      const first = !bucket.warned;
      bucket.warned = true;
      return first ? "dropped-first" : "dropped";
    }

    bucket.packets += 1;
    bucket.points += points;
    return "ok";
  }

  /** Participant left: drop their counters so a rejoin starts clean. */
  forget(identity: string): void {
    this.#state.delete(identity);
  }
}

function text(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  if (!value.length || value.length > maxLength) return undefined;
  return value;
}

function whole(value: unknown, min: number, max: number): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  if (rounded < min || rounded > max) return undefined;
  return rounded;
}

function coordinate(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return clampFrame(value);
}

function points(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  // Odd length means a truncated pair — the rest cannot be trusted either.
  if (value.length < 2 || value.length % 2 !== 0) return undefined;

  const cut = value.slice(0, MAX_POINTS_PER_MESSAGE * 2);
  const out = new Array<number>(cut.length);
  for (let i = 0; i < cut.length; i++) {
    const n = cut[i];
    if (typeof n !== "number" || !Number.isFinite(n)) return undefined;
    out[i] = clampFrame(n);
  }
  return out;
}

function identities(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const id = text(item, MAX_IDENTITY_LENGTH);
    if (id) out.push(id);
    if (out.length >= MAX_BLOCKED) break;
  }
  return out;
}
